import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Card, CardContent } from '../components/ui/card';
import ResourceCard from '../components/resources/ResourceCard';
import FilterSidebar from '../components/resources/FilterSidebar';
import { resourcesAPI, binderAPI } from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    Search, 
    Filter, 
    LayoutGrid, 
    List,
    X,
    SlidersHorizontal,
    Lock,
    LogIn,
    Map,
    Bookmark,
    FolderOpen,
    Loader2,
    Plus,
    Download,
    FileSpreadsheet,
    FileText,
    Briefcase,
    GraduationCap,
    Building2,
    Users,
    Truck,
    Heart
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DirectoryPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, isAuthenticated, loading: authLoading, login } = useAuth();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid');
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

    // Save to binder state
    const [showSaveToBinder, setShowSaveToBinder] = useState(false);
    const [saveTarget, setSaveTarget] = useState(null);
    const [binders, setBinders] = useState([]);
    const [selectedBinder, setSelectedBinder] = useState('');
    const [saveNotes, setSaveNotes] = useState('');
    const [savingToBinder, setSavingToBinder] = useState(false);
    const [loadingBinders, setLoadingBinders] = useState(false);

    const [filters, setFilters] = useState({
        state: searchParams.get('state') || '',
        organizationType: searchParams.get('type') || '',
        search: searchParams.get('search') || '',
        featuredOnly: false,
    });

    useEffect(() => {
        // Only fetch resources if user is authenticated
        if (!isAuthenticated || authLoading) {
            setLoading(false);
            return;
        }

        const fetchResources = async () => {
            setLoading(true);
            try {
                const params = {};
                if (filters.state) params.state = filters.state;
                if (filters.organizationType) params.organization_type = filters.organizationType;
                if (filters.search) params.search = filters.search;
                if (filters.featuredOnly) params.featured_only = true;

                const response = await resourcesAPI.getAll(params);
                setResources(response.data);
            } catch (error) {
                console.error('Error fetching resources:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchResources();
    }, [filters, isAuthenticated, authLoading]);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        
        // Update URL params
        const params = new URLSearchParams();
        if (newFilters.state) params.set('state', newFilters.state);
        if (newFilters.organizationType) params.set('type', newFilters.organizationType);
        if (newFilters.search) params.set('search', newFilters.search);
        setSearchParams(params);
    };

    const handleSaveToBinder = async (resource) => {
        setSaveTarget(resource);
        setSelectedBinder('');
        setSaveNotes('');
        setShowSaveToBinder(true);
        if (binders.length === 0) {
            setLoadingBinders(true);
            try {
                const res = await binderAPI.getBinders();
                setBinders(res.data || []);
            } catch {
                setBinders([]);
            } finally {
                setLoadingBinders(false);
            }
        }
    };

    const confirmSaveToBinder = async () => {
        if (!selectedBinder || !saveTarget) return;
        setSavingToBinder(true);
        try {
            await binderAPI.saveResource(selectedBinder, {
                resource_id: saveTarget.resource_id,
                notes: saveNotes || undefined
            });
            toast.success(`"${saveTarget.name}" saved to binder!`);
            setShowSaveToBinder(false);
        } catch (error) {
            if (error.response?.status === 409) {
                toast.info('Already saved to that binder');
            } else {
                toast.error(error.response?.data?.detail || 'Failed to save');
            }
        } finally {
            setSavingToBinder(false);
        }
    };

    const handleClearFilters = () => {
        setFilters({
            state: '',
            organizationType: '',
            search: '',
            featuredOnly: false,
        });
        setSearchParams({});
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const search = formData.get('search');
        handleFilterChange({ ...filters, search });
    };

    const activeFilterCount = [
        filters.state,
        filters.organizationType,
        filters.featuredOnly
    ].filter(Boolean).length;

    const [showExportMenu, setShowExportMenu] = useState(false);

    const getExportData = () => resources.map(r => ({
        Name: r.name || '',
        State: r.state || '',
        City: r.city || '',
        Types: (r.organization_types || []).join(', '),
        Website: r.website || '',
        Description: (r.description || '').slice(0, 200),
    }));

    const exportExcel = () => {
        const ws = XLSX.utils.json_to_sheet(getExportData());
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Directory');
        XLSX.writeFile(wb, 'HAAPE_Directory.xlsx');
        setShowExportMenu(false);
        toast.success('Excel file downloaded');
    };

    const exportPDF = () => {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text('HAAPE Hub - Public Directory', 14, 15);
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(`${resources.length} resources | Exported ${new Date().toLocaleDateString()}`, 14, 22);
        autoTable(doc, {
            startY: 28,
            head: [['Name', 'State', 'City', 'Types', 'Website']],
            body: resources.map(r => [
                r.name || '', r.state || '', r.city || '',
                (r.organization_types || []).join(', '),
                r.website || ''
            ]),
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [13, 148, 136] },
        });
        doc.save('HAAPE_Directory.pdf');
        setShowExportMenu(false);
        toast.success('PDF file downloaded');
    };

    // Show login required screen if not authenticated
    if (!authLoading && !isAuthenticated) {
        return (
            <div className="min-h-screen bg-background" data-testid="directory-page-login-required">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <Card className="text-center">
                        <CardContent className="pt-12 pb-10">
                            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Lock className="h-10 w-10 text-primary" />
                            </div>
                            <h1 className="font-heading text-3xl font-bold mb-4">
                                Members Only
                            </h1>
                            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                                The Public Directory is exclusively available to HAAPE members. 
                                Please log in with your member account to access our comprehensive 
                                directory of employment and support resources.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button onClick={login} size="lg" className="gap-2" data-testid="login-btn">
                                    <LogIn className="h-4 w-4" />
                                    Member Login
                                </Button>
                                <Link to="/apply">
                                    <Button variant="outline" size="lg" data-testid="apply-btn">
                                        Apply For Membership
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="directory-page">
            {/* Header */}
            <div className="bg-card border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="font-heading text-3xl font-bold mb-2">
                                Public Directory
                            </h1>
                            <p className="text-muted-foreground">
                                {loading ? 'Loading...' : `${resources.length} resources found`}
                            </p>
                        </div>

                        {/* Search */}
                        <form onSubmit={handleSearchSubmit} className="flex gap-2 w-full md:w-auto">
                            <div className="relative flex-1 md:w-80">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    name="search"
                                    type="text"
                                    placeholder="Search resources..."
                                    defaultValue={filters.search}
                                    className="pl-10"
                                    data-testid="directory-search-input"
                                />
                            </div>
                            <Button type="submit" data-testid="directory-search-btn">
                                Search
                            </Button>
                        </form>
                    </div>

                    {/* Active Filters */}
                    {(filters.state || filters.organizationType || filters.search) && (
                        <div className="flex flex-wrap items-center gap-2 mt-4">
                            <span className="text-sm text-muted-foreground">Active filters:</span>
                            {filters.state && (
                                <Badge variant="secondary" className="gap-1">
                                    {filters.state}
                                    <X 
                                        className="h-3 w-3 cursor-pointer" 
                                        onClick={() => handleFilterChange({ ...filters, state: '' })}
                                    />
                                </Badge>
                            )}
                            {filters.organizationType && (
                                <Badge variant="secondary" className="gap-1">
                                    {filters.organizationType}
                                    <X 
                                        className="h-3 w-3 cursor-pointer" 
                                        onClick={() => handleFilterChange({ ...filters, organizationType: '' })}
                                    />
                                </Badge>
                            )}
                            {filters.search && (
                                <Badge variant="secondary" className="gap-1">
                                    "{filters.search}"
                                    <X 
                                        className="h-3 w-3 cursor-pointer" 
                                        onClick={() => handleFilterChange({ ...filters, search: '' })}
                                    />
                                </Badge>
                            )}
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={handleClearFilters}
                                className="text-xs h-6"
                            >
                                Clear all
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Explore by Category */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-2">
                <h2 className="font-heading text-lg font-semibold mb-4">Explore by Category</h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
                    {[
                        { icon: Briefcase, title: 'Employers', type: 'Employer', color: 'from-teal-500 to-teal-600' },
                        { icon: GraduationCap, title: 'Training', type: 'Training Provider', color: 'from-cyan-500 to-cyan-600' },
                        { icon: Building2, title: 'Higher Ed', type: 'Higher Education', color: 'from-blue-500 to-blue-600' },
                        { icon: Users, title: 'Support Orgs', type: 'Support Organization', color: 'from-green-500 to-green-600' },
                        { icon: Truck, title: 'Transportation', type: 'Transportation', color: 'from-indigo-500 to-indigo-600' },
                        { icon: Heart, title: 'Service Providers', type: 'Service Provider', color: 'from-pink-500 to-pink-600' },
                        { icon: GraduationCap, title: 'High School', type: 'High School', color: 'from-amber-500 to-amber-600' },
                    ].map((cat) => {
                        const isActive = filters.organizationType === cat.type;
                        return (
                            <button
                                key={cat.type}
                                onClick={() => handleFilterChange({ ...filters, organizationType: isActive ? '' : cat.type })}
                                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all duration-200 ${
                                    isActive
                                        ? 'border-primary bg-primary/10 shadow-sm'
                                        : 'border-border hover:border-primary/40 hover:shadow-sm'
                                }`}
                                data-testid={`category-${cat.type.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${cat.color} flex items-center justify-center`}>
                                    <cat.icon className="h-4 w-4 text-white" />
                                </div>
                                <span className={`text-xs font-medium ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>{cat.title}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex gap-8">
                    {/* Desktop Sidebar */}
                    <div className="hidden lg:block w-64 shrink-0">
                        <FilterSidebar
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilters={handleClearFilters}
                        />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                        {/* Toolbar */}
                        <div className="flex items-center justify-between mb-6">
                            {/* Mobile Filter Button */}
                            <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
                                <SheetTrigger asChild className="lg:hidden">
                                    <Button variant="outline" size="sm" className="gap-2">
                                        <SlidersHorizontal className="h-4 w-4" />
                                        Filters
                                        {activeFilterCount > 0 && (
                                            <Badge variant="secondary" className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                                                {activeFilterCount}
                                            </Badge>
                                        )}
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="left" className="w-80 p-0">
                                    <div className="p-4">
                                        <FilterSidebar
                                            filters={filters}
                                            onFilterChange={(newFilters) => {
                                                handleFilterChange(newFilters);
                                                setMobileFilterOpen(false);
                                            }}
                                            onClearFilters={() => {
                                                handleClearFilters();
                                                setMobileFilterOpen(false);
                                            }}
                                        />
                                    </div>
                                </SheetContent>
                            </Sheet>

                            {/* View Toggle & Export */}
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1 bg-muted rounded-md p-1">
                                    <Button
                                        variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                                        size="sm"
                                        onClick={() => setViewMode('grid')}
                                        className="h-8 w-8 p-0"
                                        data-testid="view-mode-grid"
                                    >
                                        <LayoutGrid className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                                        size="sm"
                                        onClick={() => setViewMode('list')}
                                        className="h-8 w-8 p-0"
                                        data-testid="view-mode-list"
                                    >
                                        <List className="h-4 w-4" />
                                    </Button>
                                </div>
                                <div className="relative">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="gap-2"
                                        onClick={() => setShowExportMenu(!showExportMenu)}
                                        data-testid="export-btn"
                                    >
                                        <Download className="h-4 w-4" />
                                        Export
                                    </Button>
                                    {showExportMenu && (
                                        <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-md shadow-lg z-50 w-44">
                                            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors rounded-t-md" onClick={exportExcel} data-testid="export-excel">
                                                <FileSpreadsheet className="h-4 w-4 text-green-600" />
                                                Export as Excel
                                            </button>
                                            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors rounded-b-md" onClick={exportPDF} data-testid="export-pdf">
                                                <FileText className="h-4 w-4 text-red-600" />
                                                Export as PDF
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <Link to="/map">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="gap-2"
                                        data-testid="view-mode-map"
                                    >
                                        <Map className="h-4 w-4" />
                                        Map View
                                    </Button>
                                </Link>
                            </div>
                        </div>

                        {/* Results */}
                        {loading ? (
                            <div className={`grid gap-6 ${viewMode === 'grid' ? 'sm:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="bg-card border border-border rounded-lg p-5">
                                        <Skeleton className="h-5 w-3/4 mb-3" />
                                        <Skeleton className="h-4 w-1/2 mb-4" />
                                        <Skeleton className="h-16 w-full mb-4" />
                                        <div className="flex gap-2">
                                            <Skeleton className="h-6 w-20" />
                                            <Skeleton className="h-6 w-24" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : resources.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Search className="h-8 w-8 text-muted-foreground" />
                                </div>
                                <h3 className="font-heading font-semibold text-lg mb-2">No resources found</h3>
                                <p className="text-muted-foreground mb-4">
                                    Try adjusting your filters or search terms
                                </p>
                                <Button variant="outline" onClick={handleClearFilters}>
                                    Clear Filters
                                </Button>
                            </div>
                        ) : (
                            <div className={`grid gap-6 ${viewMode === 'grid' ? 'sm:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
                                {resources.map((resource, index) => (
                                    <div 
                                        key={resource.resource_id}
                                        className="animate-fade-in"
                                        style={{ animationDelay: `${Math.min(index * 0.05, 0.3)}s` }}
                                    >
                                        <ResourceCard 
                                            resource={resource} 
                                            isAuthenticated={isAuthenticated}
                                            onSaveToBinder={handleSaveToBinder}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Save to Binder Dialog */}
            <Dialog open={showSaveToBinder} onOpenChange={setShowSaveToBinder}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FolderOpen className="h-5 w-5 text-primary" />
                            Save to Binder
                        </DialogTitle>
                        <DialogDescription>
                            Save "{saveTarget?.name}" to one of your digital binders.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {loadingBinders ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : binders.length === 0 ? (
                            <div className="text-center py-4" data-testid="no-binders-directory">
                                <FolderOpen className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                                <p className="text-sm text-muted-foreground mb-3">You don't have any binders yet.</p>
                                <Button size="sm" variant="outline" asChild>
                                    <a href="/binder"><Plus className="h-4 w-4 mr-1" />Create a Binder</a>
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-2">
                                    <Label>Select Binder</Label>
                                    <Select value={selectedBinder} onValueChange={setSelectedBinder}>
                                        <SelectTrigger data-testid="directory-binder-select">
                                            <SelectValue placeholder="Choose a binder..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {binders.map(b => (
                                                <SelectItem key={b.binder_id} value={b.binder_id}>
                                                    {b.name}{b.loved_one_name ? ` (${b.loved_one_name})` : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Notes (optional)</Label>
                                    <Textarea
                                        placeholder="Why are you saving this resource?"
                                        rows={2}
                                        value={saveNotes}
                                        onChange={e => setSaveNotes(e.target.value)}
                                        data-testid="directory-save-notes"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    {binders.length > 0 && (
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowSaveToBinder(false)}>Cancel</Button>
                            <Button onClick={confirmSaveToBinder} disabled={savingToBinder || !selectedBinder} data-testid="directory-confirm-save">
                                {savingToBinder ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bookmark className="h-4 w-4 mr-2" />}
                                Save to Binder
                            </Button>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DirectoryPage;
